import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Plus, Route } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCarePathwayMutations } from '@/features/patient-record/hooks/useCarePathwayMutations';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  CarePathwayStatus,
  CarePathwayViewModel,
} from '@/features/patient-record/types';

import { ClinicalSection, type ClinicalSectionLabels } from '../ClinicalSection';
import { ConfirmDeleteDialog } from '../_shared/ConfirmDeleteDialog';

import { AssignPathwayDialog } from './AssignPathwayDialog';
import { CarePathwayRow } from './CarePathwayRow';
import { ReasonDialog } from './ReasonDialog';

interface CarePathwaysSectionProps {
  readonly guid: string;
}

type StepDialogKind = 'complete' | 'skip';
interface StepDialogState {
  readonly kind: StepDialogKind;
  readonly pathwayId: string;
  readonly stepId: string;
}

interface CloseDialogState {
  readonly pathwayId: string;
  readonly action: 'COMPLETED' | 'CANCELLED';
}

const CLOSED_STATUSES = new Set<CarePathwayStatus>(['COMPLETED', 'CANCELLED']);

export function CarePathwaysSection({ guid }: CarePathwaysSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery(patientQueries.carePathways(guid));
  const [assignOpen, setAssignOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<CarePathwayViewModel | null>(null);
  const [stepDialog, setStepDialog] = useState<StepDialogState | null>(null);
  const [closeDialog, setCloseDialog] = useState<CloseDialogState | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const { remove, updateStep, updateStatus } = useCarePathwayMutations(guid);

  const all = useMemo<readonly CarePathwayViewModel[]>(
    () => query.data ?? [],
    [query.data],
  );
  const { active, closed } = useMemo(() => {
    const a: CarePathwayViewModel[] = [];
    const c: CarePathwayViewModel[] = [];
    for (const cp of all) {
      if (CLOSED_STATUSES.has(cp.status)) c.push(cp);
      else a.push(cp);
    }
    return { active: a, closed: c };
  }, [all]);

  const pathwayCount = all.length;

  const labels: ClinicalSectionLabels = {
    title: t('patient.carePathways.heading'),
    titleSuffix:
      pathwayCount > 0 ? (
        <span className="text-xs font-normal text-muted-foreground">
          {t('patient.carePathways.pathwayCount', { count: pathwayCount })}
        </span>
      ) : undefined,
    empty: t('patient.carePathways.empty'),
    emptyIcon: <Route className="h-5 w-5" aria-hidden="true" />,
    loading: t('patient.carePathways.loading'),
    errorHeading: t('patient.carePathways.error.heading'),
    errorMessage: t('patient.carePathways.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  const renderRow = (cp: CarePathwayViewModel): ReactElement => (
    <CarePathwayRow
      guid={guid}
      carePathway={cp}
      updatingStepId={
        updateStep.isPending && updateStep.variables.pathwayId === cp.id
          ? updateStep.variables.stepId
          : null
      }
      statusBusy={
        updateStatus.isPending && updateStatus.variables.pathwayId === cp.id
      }
      onRequestStepComplete={({ stepId }) => {
        setStepDialog({ kind: 'complete', pathwayId: cp.id, stepId });
      }}
      onRequestStepSkip={({ stepId }) => {
        setStepDialog({ kind: 'skip', pathwayId: cp.id, stepId });
      }}
      onMarkStepIncomplete={(stepId) => {
        updateStep.mutate({ pathwayId: cp.id, stepId, status: 'NOT_STARTED' });
      }}
      onChangeStatus={(status) => {
        updateStatus.mutate({ pathwayId: cp.id, status });
      }}
      onRequestClose={(action) => {
        setCloseDialog({ pathwayId: cp.id, action });
      }}
      onRemove={() => {
        setPendingRemove(cp);
      }}
    />
  );

  return (
    <>
      <ClinicalSection
        id="care-pathways"
        labels={labels}
        query={
          /* Drive the section off `active` only — closed pathways live behind
             the toggle below so the section's empty-state messaging matches the
             active-only view. */
          { ...query, data: active }
        }
        summary={all.map((cp) => {
          const status = t(`patient.carePathways.status.${cp.status}`);
          const value =
            cp.stepsTotal > 0
              ? `${status} · ${String(cp.stepsCompleted)}/${String(cp.stepsTotal)} ${t('patient.carePathways.stepsSuffix')}`
              : status;
          return { label: cp.name, value, filled: true };
        })}
        itemKey={(cp) => cp.id}
        renderItem={renderRow}
        headerAction={
          <Button
            size="sm"
            onClick={() => {
              setAssignOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('patient.carePathways.assign')}
          </Button>
        }
      />

      {closed.length > 0 && (
        <div className="border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={() => {
              setShowClosed((v) => !v);
            }}
            aria-expanded={showClosed}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              aria-hidden="true"
              className={cn('h-3.5 w-3.5 transition-transform', !showClosed && '-rotate-90')}
            />
            {t('patient.carePathways.showClosed', { count: closed.length })}
          </button>
          {showClosed && (
            <ul className="mt-3 space-y-4">
              {closed.map((cp) => (
                <li key={cp.id}>{renderRow(cp)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {assignOpen && (
        <AssignPathwayDialog
          guid={guid}
          onClose={() => {
            setAssignOpen(false);
          }}
        />
      )}

      {stepDialog !== null && (
        <ReasonDialog
          title={
            stepDialog.kind === 'skip'
              ? t('patient.carePathways.skipDialog.title')
              : t('patient.carePathways.completeDialog.title')
          }
          description={
            stepDialog.kind === 'skip'
              ? t('patient.carePathways.skipDialog.description')
              : t('patient.carePathways.completeDialog.description')
          }
          fieldLabel={
            stepDialog.kind === 'skip'
              ? t('patient.carePathways.skipDialog.fieldLabel')
              : t('patient.carePathways.completeDialog.fieldLabel')
          }
          fieldPlaceholder={
            stepDialog.kind === 'skip'
              ? t('patient.carePathways.skipDialog.placeholder')
              : t('patient.carePathways.completeDialog.placeholder')
          }
          confirmLabel={
            stepDialog.kind === 'skip'
              ? t('patient.carePathways.skipDialog.confirm')
              : t('patient.carePathways.completeDialog.confirm')
          }
          requireText={stepDialog.kind === 'skip'}
          busy={updateStep.isPending}
          destructive={stepDialog.kind === 'skip'}
          onCancel={() => {
            setStepDialog(null);
          }}
          onConfirm={(text) => {
            const variables =
              stepDialog.kind === 'skip'
                ? {
                    pathwayId: stepDialog.pathwayId,
                    stepId: stepDialog.stepId,
                    status: 'SKIPPED' as const,
                    skipRationale: text,
                  }
                : {
                    pathwayId: stepDialog.pathwayId,
                    stepId: stepDialog.stepId,
                    status: 'COMPLETED' as const,
                    ...(text.length > 0 ? { completionNote: text } : {}),
                  };
            updateStep.mutate(variables, {
              onSettled: () => {
                setStepDialog(null);
              },
            });
          }}
        />
      )}

      {closeDialog !== null && (
        <ReasonDialog
          title={
            closeDialog.action === 'COMPLETED'
              ? t('patient.carePathways.completePathwayDialog.title')
              : t('patient.carePathways.cancelDialog.title')
          }
          description={
            closeDialog.action === 'COMPLETED'
              ? t('patient.carePathways.completePathwayDialog.description')
              : t('patient.carePathways.cancelDialog.description')
          }
          fieldLabel={t('patient.carePathways.closeReasonLabel')}
          fieldPlaceholder={t('patient.carePathways.closeReasonPlaceholder')}
          confirmLabel={
            closeDialog.action === 'COMPLETED'
              ? t('patient.carePathways.completePathwayDialog.confirm')
              : t('patient.carePathways.cancelDialog.confirm')
          }
          requireText
          busy={updateStatus.isPending}
          destructive={closeDialog.action === 'CANCELLED'}
          onCancel={() => {
            setCloseDialog(null);
          }}
          onConfirm={(reason) => {
            updateStatus.mutate(
              { pathwayId: closeDialog.pathwayId, status: closeDialog.action, reason },
              {
                onSettled: () => {
                  setCloseDialog(null);
                },
              },
            );
          }}
        />
      )}

      {pendingRemove !== null && (
        <ConfirmDeleteDialog
          title={t('patient.carePathways.confirmRemoveTitle')}
          message={t('patient.carePathways.confirmRemoveMessage', {
            name: pendingRemove.name,
          })}
          confirmLabel={t('patient.carePathways.remove')}
          busy={remove.isPending}
          onCancel={() => {
            setPendingRemove(null);
          }}
          onConfirm={() => {
            remove.mutate(pendingRemove.id, {
              onSettled: () => {
                setPendingRemove(null);
              },
            });
          }}
        />
      )}
    </>
  );
}
